import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, TextField, IconButton, Button, Chip, Paper,
  Autocomplete, Tooltip,
} from '@mui/material';
import { DeleteOutline, TrendingUpOutlined } from '@mui/icons-material';

import SectionCard from './SectionCard';
import { TableCard, GroupLabel } from '../tabLayout';
import SearchableSelect from '../../common/SearchableSelect';
import { ToneChip } from '../../common/StatusChip';
import HelpTip from '../../common/HelpTip';
import { AddClassButtons, CopyToAllButton, Fact, FactGrid, ListRow, QuietNote, SchoolClass, copyToAll, useClassNames } from './sectionParts';
import dateTimeUtils from '../../../utils/dateTimeUtils';
import { ageLimitText, birthWindow } from '../../../utils/age';
import AgeLimitInput from './AgeLimitInput';
import { AdmissionCycle, EligibilityRule } from '../../../types/admissions';

interface Props {
  cycle: AdmissionCycle;
  classes: SchoolClass[];
  loadingClasses: boolean;
  canEdit: boolean;
  onSave: (patch: Partial<AdmissionCycle>) => Promise<void>;
}

/**
 * Per-class eligibility rules. Four rule kinds (age / min marks / required
 * subjects / gender), hard-fail triggers Ineligible on public submit, warn
 * annotates the application for staff review but proceeds.
 */

const GENDER_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'male', label: 'Boys only' },
  { value: 'female', label: 'Girls only' },
];

const genderLabel = (g?: EligibilityRule['gender']): string =>
  GENDER_OPTIONS.find(o => o.value === (g || 'any'))?.label ?? 'Any';

/** "2y 6m – 3y 6m", then when it's measured and who it lets in. */
const AgeFact: React.FC<{ ageOn: EligibilityRule['ageOn'] }> = ({ ageOn }) => {
  const limit = ageLimitText(ageOn?.minMonths, ageOn?.maxMonths);
  if (!ageOn?.date || !limit) return <NotRequired />;
  return (
    <>
      {limit}
      <Typography variant="caption" color="text.secondary" display="block">
        on {dateTimeUtils.formatDateForDisplay(ageOn.date)}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {birthWindow(ageOn.date, ageOn.minMonths, ageOn.maxMonths)}
      </Typography>
    </>
  );
};

const AGE_HELP = {
  title: 'Age on a date',
  text: "The child's age is counted in completed years and months on this date — usually the day classes begin.",
  footnote: '3 years 6 months 29 days still counts as 3 years 6 months.',
};

/** The age limit moved `years` up, on the same date. */
const olderBy = (ageOn: NonNullable<EligibilityRule['ageOn']>, years: number): EligibilityRule['ageOn'] => ({
  date: ageOn.date,
  minMonths: ageOn.minMonths != null ? ageOn.minMonths + years * 12 : undefined,
  maxMonths: ageOn.maxMonths != null ? ageOn.maxMonths + years * 12 : undefined,
});

/** Fills the classes after this one from its age limit, a year older each. */
const StepUpButton: React.FC<{ count: number; onClick: () => void }> = ({ count, onClick }) => (count === 0 ? null : (
  <Button
    size="small"
    startIcon={<TrendingUpOutlined fontSize="small" />}
    onClick={onClick}
    sx={{ mt: 1, ml: -0.75, textTransform: 'none' }}
  >
    Fill the {count === 1 ? 'next class' : `next ${count} classes`}, a year older each
  </Button>
));

const NotRequired: React.FC = () => (
  <Typography component="span" variant="body2" color="text.disabled">Not required</Typography>
);

/** Under the age inputs: who the limit lets in, or what's still missing. */
const AgeHint: React.FC<{ ageOn: EligibilityRule['ageOn'] }> = ({ ageOn }) => {
  const min = ageOn?.minMonths;
  const max = ageOn?.maxMonths;
  if (min == null && max == null) return null;
  let text: string | null;
  let tone: 'text.secondary' | 'error.main' = 'text.secondary';
  if (min != null && max != null && min > max) { text = 'The minimum age is above the maximum.'; tone = 'error.main'; }
  else if (!ageOn?.date) { text = 'Set the date the age is measured on — the limit is not applied without it.'; tone = 'error.main'; }
  else text = birthWindow(ageOn.date, min, max);
  return <Typography variant="caption" color={tone} sx={{ display: 'block', mt: 1 }}>{text}</Typography>;
};

const EligibilitySection: React.FC<Props> = ({ cycle, classes, loadingClasses, canEdit, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<EligibilityRule[]>([]);
  const { nameOf, options: classOptions } = useClassNames(classes);

  useEffect(() => {
    if (editing) setDraft(cycle.eligibility ?? []);
  }, [editing, cycle.eligibility]);

  const availableClasses = useMemo(() => {
    const used = new Set(draft.map(r => r.classId));
    return classes.filter(c => !used.has(c._id));
  }, [classes, draft]);

  const addRows = (add: SchoolClass[]) => setDraft(d => [
    ...d,
    ...add.map(c => ({ classId: c._id, ageOn: null, minPreviousPercentage: null, requiredSubjects: [], gender: 'any' as const })),
  ]);

  const copyRule = (idx: number) => setDraft(d => copyToAll(d, idx, r => ({
    ageOn: r.ageOn, minPreviousPercentage: r.minPreviousPercentage, requiredSubjects: r.requiredSubjects, gender: r.gender,
  })));

  // School order of each class, so "the classes below" means the next ones up.
  const order = useMemo(() => new Map(classes.map((c, i) => [c._id, i])), [classes]);
  const laterRows = (idx: number): number[] => draft
    .map((r, i) => i)
    .filter(i => (order.get(draft[i].classId) ?? -1) > (order.get(draft[idx].classId) ?? -1))
    .sort((a, b) => (order.get(draft[a].classId) ?? 0) - (order.get(draft[b].classId) ?? 0));

  // Each later class takes this age limit, a year older per step.
  const stepUp = (idx: number) => {
    const base = draft[idx].ageOn;
    if (!base) return;
    const later = laterRows(idx);
    setDraft(d => d.map((r, i) => (later.includes(i) ? { ...r, ageOn: olderBy(base, later.indexOf(i) + 1) } : r)));
  };

  const update = (idx: number, patch: Partial<EligibilityRule>) => {
    setDraft(draft.map((row, i) => i === idx ? { ...row, ...patch } : row));
  };

  // Clearing the date and both limits removes the age rule altogether.
  const setAge = (idx: number, patch: Partial<NonNullable<EligibilityRule['ageOn']>>) => {
    const next = { ...(draft[idx].ageOn || { date: '' }), ...patch };
    const empty = !next.date && next.minMonths == null && next.maxMonths == null;
    update(idx, { ageOn: empty ? null : next });
  };

  const remove = (idx: number) => setDraft(draft.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ eligibility: draft });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const rows = editing ? draft : (cycle.eligibility ?? []);

  return (
    <SectionCard
      title="Eligibility rules"
      description="Who may apply to each class. Applications that miss a rule are marked ineligible, with the reason shown to the parent."
      canEdit={canEdit}
      editing={editing}
      onEditToggle={setEditing}
      onSave={save}
      onCancel={() => setDraft(cycle.eligibility ?? [])}
      saving={saving}
      headerActions={<AddClassButtons label="Add rule" remaining={availableClasses} onAdd={addRows} disabled={loadingClasses} />}
    >
      {rows.length === 0 ? (
        <QuietNote>No eligibility rules — every application is accepted at this step.</QuietNote>
      ) : !editing ? (
        <TableCard>
          {rows.map((rule, idx) => (
            <ListRow key={`${rule.classId}-${idx}`} sx={{ py: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>{nameOf(rule.classId)}</Typography>
                <FactGrid columns={4}>
                  <Fact label="Age"><AgeFact ageOn={rule.ageOn} /></Fact>
                  <Fact label="Previous marks">
                    {rule.minPreviousPercentage != null ? `At least ${rule.minPreviousPercentage}%` : <NotRequired />}
                  </Fact>
                  <Fact label="Gender">{genderLabel(rule.gender)}</Fact>
                  <Fact label="Subjects studied">
                    {(rule.requiredSubjects || []).length === 0 ? <NotRequired /> : (
                      <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5 }}>
                        {rule.requiredSubjects!.map(s => <ToneChip key={s} label={s} tone="neutral" />)}
                      </Stack>
                    )}
                  </Fact>
                </FactGrid>
              </Box>
            </ListRow>
          ))}
        </TableCard>
      ) : (
        <Stack spacing={2}>
          {rows.map((rule, idx) => (
            <Paper key={`${rule.classId}-${idx}`} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              {/* Header row: class picker + remove button */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
                <SearchableSelect
                  label="Class"
                  value={rule.classId}
                  onChange={(v) => update(idx, { classId: v })}
                  options={classOptions.filter(o => o.value === rule.classId || availableClasses.some(c => c._id === o.value))}
                  clearable={false}
                  minWidth={220}
                />
                <Stack direction="row">
                  {rows.length > 1 && <CopyToAllButton title="Use this rule for every class" onClick={() => copyRule(idx)} />}
                  <Tooltip title="Remove rule">
                    <IconButton size="small" color="error" onClick={() => remove(idx)}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>

              <Stack spacing={2.5}>
                {/* Age on a cut-off date, in years and months */}
                <Box>
                  <GroupLabel>
                    <HelpTip label="Age on a date" {...AGE_HELP} />
                  </GroupLabel>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
                    <TextField
                      type="date"
                      size="small"
                      label="Age on"
                      value={rule.ageOn?.date ? String(rule.ageOn.date).slice(0, 10) : ''}
                      onChange={(e) => setAge(idx, { date: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: { sm: 180 } }}
                    />
                    <AgeLimitInput label="Minimum age" value={rule.ageOn?.minMonths} onChange={(v) => setAge(idx, { minMonths: v })} />
                    <AgeLimitInput label="Maximum age" value={rule.ageOn?.maxMonths} onChange={(v) => setAge(idx, { maxMonths: v })} />
                  </Stack>
                  <AgeHint ageOn={rule.ageOn} />
                  {rule.ageOn?.date && (rule.ageOn.minMonths != null || rule.ageOn.maxMonths != null) && (
                    <StepUpButton count={laterRows(idx).length} onClick={() => stepUp(idx)} />
                  )}
                </Box>

                {/* Min previous % + gender in one row */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <GroupLabel>Minimum previous %</GroupLabel>
                    <TextField
                      type="number"
                      size="small"
                      fullWidth
                      value={rule.minPreviousPercentage ?? ''}
                      onChange={(e) => update(idx, { minPreviousPercentage: e.target.value === '' ? null : parseFloat(e.target.value) })}
                      inputProps={{ min: 0, max: 100, step: 0.5 }}
                      placeholder="Not required"
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <GroupLabel>Gender</GroupLabel>
                    <SearchableSelect
                      value={rule.gender || 'any'}
                      onChange={(v) => update(idx, { gender: v as EligibilityRule['gender'] })}
                      options={GENDER_OPTIONS}
                      clearable={false}
                      fullWidth
                    />
                  </Box>
                </Stack>

                {/* Required subjects */}
                <Box>
                  <GroupLabel>Required previous subjects</GroupLabel>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={rule.requiredSubjects || []}
                    onChange={(_, v) => update(idx, { requiredSubjects: v as string[] })}
                    renderTags={(value, getTagProps) =>
                      value.map((option, i) => (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={option as string}
                          {...getTagProps({ index: i })}
                          key={option as string}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} size="small" placeholder="Type and press Enter…" />
                    )}
                  />
                </Box>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </SectionCard>
  );
};

export default EligibilitySection;
